import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, List, Map as MapIcon, RefreshCw, CalendarPlus, Globe2 } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import { useAuth } from "../AuthContext";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import EmailCapture from "../components/EmailCapture";
import { EventCard } from "../components/events/EventCard";
import { EventFilters } from "../components/events/EventFilters";
import { EventCalendar } from "../components/events/EventCalendar";
import { EventMap } from "../components/events/EventMap";
import { ProposeEventBlock } from "../components/events/ProposeEventBlock";
import { BACKEND_URL } from "../lib/backend";
import { setCanonical, setDescription, setJsonLd, SITE_URL } from "../lib/seo";
import { FILTER_KEYS, VIEWS, monthLabel, organizerLd, readFilters, sameWeekCount, todayYm } from "../lib/events";

export default function EventsPage() {
  const [dm] = useDarkMode();
  const { user } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const filters = useMemo(() => readFilters(params), [params]);
  const view = VIEWS.includes(params.get("view")) ? params.get("view") : "list";
  const calMonth = params.get("cal") || filters.month || todayYm();

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const setView = (v) => { const next = new URLSearchParams(params); if (v === "list") next.delete("view"); else next.set("view", v); setParams(next, { replace: true }); };
  const clear = () => { const next = new URLSearchParams(params); FILTER_KEYS.forEach((k) => next.delete(k)); setParams(next, { replace: true }); };

  useEffect(() => {
    const qs = new URLSearchParams();
    FILTER_KEYS.forEach((k) => { if (filters[k]) qs.set(k === "closing" ? "closing" : k, k === "closing" ? "true" : filters[k]); });
    setError(false);
    axios.get(`${BACKEND_URL}/api/events?${qs.toString()}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [filters]);

  useEffect(() => {
    const y = new Date().getFullYear();
    document.title = `Travel Trade Shows & Industry Events ${y}–${y + 1}: Map, Calendar & List | TourismIntel`;
    const c1 = setDescription("Travel & tourism industry events calendar: trade shows, conferences, summits and MICE events worldwide with official dates, venues, access type and why they matter — map, monthly calendar and list, plus a subscribable .ics feed.");
    setCanonical(`${SITE_URL}/events`);
    return c1;
  }, []);

  useEffect(() => {
    const evs = data?.events || [];
    if (!evs.length) return;
    const ld = {
      "@context": "https://schema.org", "@type": "ItemList", name: "Travel & Tourism Industry Events Calendar", url: `${SITE_URL}/events`,
      numberOfItems: evs.length,
      itemListElement: evs.slice(0, 100).map((ev, i) => ({
        "@type": "ListItem", position: i + 1,
        item: {
          "@type": "Event", name: ev.name, url: ev.web_url, startDate: ev.start_date, endDate: ev.end_date || ev.start_date,
          eventStatus: "https://schema.org/EventScheduled", eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          image: `${SITE_URL}/api/og/event/${ev.slug}`,
          description: ev.why_it_matters || `${ev.name} — ${ev.event_type_label}${ev.city ? ` in ${ev.city}` : ""}. Official dates from the organiser.`,
          ...(ev.official_url ? { sameAs: ev.official_url } : {}),
          ...(organizerLd(ev) ? { organizer: organizerLd(ev) } : {}),
          ...(ev.city || ev.venue ? { location: { "@type": "Place", name: ev.venue || ev.city, address: { "@type": "PostalAddress", addressLocality: ev.city, addressCountry: ev.country_code || ev.country } } } : {}),
        },
      })),
    };
    return setJsonLd("events-jsonld", ld);
  }, [data]);

  const c = dm
    ? { bg: "bg-[#0f1718]", card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", accent: "text-cyan-400", tab: "bg-[#1a2324] text-slate-300", tabOn: "bg-cyan-400 text-[#001f24]" }
    : { bg: "bg-[#f5f6f7]", card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", accent: "text-[#006874]", tab: "bg-white text-[#3b494c] shadow-sm", tabOn: "bg-[#006874] text-white" };

  const events = useMemo(() => data?.events || [], [data]);
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const ev of events) {
      const key = ev.start_date.slice(0, 7);
      if (!cur || cur.key !== key) { cur = { key, events: [] }; out.push(cur); }
      cur.events.push(ev);
    }
    return out;
  }, [events]);
  const y = new Date().getFullYear();

  return (
    <div className={`min-h-screen flex flex-col ${c.bg}`}>
      <PublicPageNav />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10" data-testid="events-page">
        <header className="mb-8 max-w-3xl">
          <p className={`category-tag text-xs mb-3 ${c.accent}`}>INDUSTRY CALENDAR</p>
          <h1 className={`editorial-heading text-3xl sm:text-4xl font-semibold ${c.text}`} data-testid="events-page-title">
            Travel trade shows &amp; industry events {y}–{y + 1}
          </h1>
          <p className={`mt-3 text-sm leading-relaxed ${c.muted}`}>
            Conferences, B2B trade shows, marketplaces, MICE and institutional summits for the travel, tourism and hospitality
            industry — with official dates, venue, access type and a short note on why each one matters for DMOs, hotels and
            travel-tech teams. Dates come from organiser announcements only; nothing is estimated.
          </p>
          {data && (
            <p className={`mt-3 text-xs font-semibold ${c.accent}`} data-testid="events-counters">
              {data.total_upcoming} upcoming event{data.total_upcoming !== 1 ? "s" : ""} · {data.countries} countries
              {data.closing_soon ? ` · ${data.closing_soon} with registration closing soon` : ""}
            </p>
          )}
        </header>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-1.5 p-1 rounded-xl" data-testid="events-view-switcher">
            {[["list", List, "List"], ["calendar", CalendarDays, "Calendar"], ["map", MapIcon, "Map"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`events-view-${v}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${view === v ? c.tabOn : c.tab}`}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>
          <a href={`${BACKEND_URL}/api/events/calendar.ics${filters.region || filters.type ? `?${new URLSearchParams({ ...(filters.region ? { region: filters.region } : {}), ...(filters.type ? { type: filters.type } : {}) })}` : ""}`}
            data-testid="events-subscribe-ics" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg ${c.tab}`} title="Subscribe in Google / Outlook / Apple Calendar">
            <CalendarPlus className="w-3.5 h-3.5" /> Subscribe (.ics)
          </a>
        </div>

        <div className="mb-6">
          <EventFilters filters={filters} setFilter={setFilter} clear={clear} taxonomy={data?.taxonomy} facets={data?.facets} dm={dm} closingSoon={data?.closing_soon} />
        </div>

        {error && <p className={`text-sm ${c.muted}`} data-testid="events-error">Could not load events — please try again later.</p>}
        {!data && !error && <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${c.accent}`} /></div>}
        {data && events.length === 0 && (
          <p className={`text-sm ${c.muted}`} data-testid="events-empty">
            No events match these filters. <button onClick={clear} className={`underline ${c.accent}`}>Clear filters</button>
          </p>
        )}

        {data && view === "list" && (
          <div className="space-y-10" data-testid="events-list">
            {groups.map((g) => (
              <section key={g.key} id={g.key} data-testid={`events-month-${g.key}`}>
                <h2 className={`category-tag text-xs mb-4 flex items-center gap-2 ${c.muted}`}>
                  <CalendarDays className="w-3.5 h-3.5" />{monthLabel(g.key).toUpperCase()} · {g.events.length}
                </h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {g.events.map((ev) => <EventCard key={ev.id} ev={ev} dm={dm} sameWeek={sameWeekCount(ev, events)} />)}
                </div>
              </section>
            ))}
          </div>
        )}

        {data && view === "calendar" && (
          <EventCalendar events={events} dm={dm} month={calMonth} setMonth={(m) => setFilter("cal", m)} today={data.today} />
        )}

        {data && view === "map" && <EventMap events={events} dm={dm} />}

        {!user && (
        <section data-testid="events-signup-cta" className={`mt-12 rounded-2xl border p-6 sm:p-8 ${c.card} ${c.border}`}>
          <div className="flex items-start gap-3 mb-4">
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? "bg-cyan-400/10" : "bg-[#006874]/8"}`}><Globe2 className={`w-4 h-4 ${c.accent}`} /></span>
            <div>
              <h2 className={`text-base font-semibold ${c.text}`}>Get the weekly Travel Brief — events included</h2>
              <p className={`mt-1 text-sm leading-relaxed ${c.muted}`}>
                Free, one email a week: the stories that matter, the reports worth reading and the events coming up. Personal event
                planner, registration-deadline reminders and a custom .ics feed are coming for members.
              </p>
            </div>
          </div>
          <EmailCapture darkMode={dm} source="events_page" />
          <p className={`mt-4 text-[11px] ${c.muted}`}>
            Looking for reports by the organisers of these events? Browse the <Link to="/reports" className={`underline ${c.accent}`}>Reports library</Link>.
          </p>
        </section>
        )}
        {user && (
          <p data-testid="events-member-note" className={`mt-12 text-[11px] ${c.muted}`}>
            Looking for reports by the organisers of these events? Browse the <Link to="/reports" className={`underline ${c.accent}`}>Reports library</Link>.
          </p>
        )}
        <ProposeEventBlock dm={dm} compact />
      </main>
      <PublicPageFooter />
    </div>
  );
}
